import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { FaUsers, FaCrown, FaStar, FaUserShield, FaSearch, FaPlus, FaKey, FaTrash, FaGift, FaPlusCircle, FaTimesCircle, FaSortAmountDown, FaSortAmountUp, FaSort, FaCheckCircle } from 'react-icons/fa';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { getErrorMessage } from '../utils/errorMessage';

// Internal / staff / demo accounts — always pinned to top of the list
// and visually highlighted to distinguish from real customers.
const INTERNAL_USER_EMAILS = new Set([
  'kimesav@gmail.com',
  'ulla@itopgaver.dk',
  'mya_h@hotmail.com',
  'demo@slushbook.dk',
  'kimv@itopgaver.dk',
]);
const isInternalUser = (m) => INTERNAL_USER_EMAILS.has((m?.email || '').toLowerCase());

const MembersPage = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc'); // name_asc | name_desc | date_desc | date_asc | status

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    // Try to fetch members - backend will handle authorization
    fetchMembers();
  }, [authLoading]);

  const fetchMembers = async () => {
    try {
      // Get session_token from localStorage for authentication
      const sessionToken = localStorage.getItem('session_token');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await axios.get(`${API}/admin/members`, {
        withCredentials: true,
        headers
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
      
      // If 403, user is not admin - redirect to home
      if (error.response?.status === 403) {
        toast.error('Kun admin har adgang til denne side');
        navigate('/');
      } else {
        toast.error('Kunne ikke hente medlemmer');
      }
    } finally {
      setLoading(false);
    }
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', role: 'user' });
  const [resetPassword, setResetPassword] = useState('');

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/members/${userId}/role`, 
        { role: newRole },
        { withCredentials: true }
      );
      toast.success('Brugerrolle opdateret!');
      fetchMembers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Kunne ikke opdatere rolle');
    }
  };

  const updateUserSubscription = async (userId, newSubscription) => {
    try {
      await axios.put(`${API}/admin/members/${userId}/subscription`,
        { subscription_type: newSubscription },
        { withCredentials: true }
      );
      toast.success('Abonnement opdateret!');
      fetchMembers();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error(error?.response?.data?.detail || 'Kunne ikke opdatere abonnement');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/members/create`, createForm, {
        withCredentials: true
      });
      toast.success('Bruger oprettet!');
      setIsCreateModalOpen(false);
      setCreateForm({ email: '', name: '', password: '', role: 'user' });
      fetchMembers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(getErrorMessage(error, 'Kunne ikke oprette bruger'));
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/members/${selectedUser.id}/reset-password`, 
        { password: resetPassword },
        { withCredentials: true }
      );
      toast.success('Password nulstillet!');
      setIsResetModalOpen(false);
      setResetPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(getErrorMessage(error, 'Kunne ikke nulstille password'));
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Er du sikker på at du vil slette ${memberName}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/members/${memberId}`, {
        withCredentials: true
      });
      toast.success('Medlem slettet');
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Kunne ikke slette medlem');
    }
  };

  // ---- Manual Pro grant handlers (launch campaign) ----
  const authHeaders = () => {
    const t = localStorage.getItem('session_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const handleGrantPro = async (member, months = 6) => {
    if (!window.confirm(`Tildel ${months} måneders gratis Pro-adgang til ${member.name}? (Ingen Stripe-betaling)`)) return;
    const note = window.prompt('Valgfri note (fx kampagne-navn):', 'Første 10 brugere kampagne') || '';
    try {
      await axios.post(
        `${API}/admin/members/${member.id || member.email}/grant-pro`,
        { months, note },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success(`${months} mdr. gratis Pro tildelt til ${member.name}`);
      fetchMembers();
    } catch (error) {
      console.error('Error granting pro:', error);
      toast.error(getErrorMessage(error, 'Kunne ikke tildele Pro'));
    }
  };

  const handleExtendPro = async (member, months = 3) => {
    const input = window.prompt(`Hvor mange måneder vil du forlænge ${member.name}'s manuelle Pro-adgang med?`, String(months));
    if (!input) return;
    const m = parseInt(input, 10);
    if (!m || m < 1 || m > 36) {
      toast.error('Ugyldig værdi — skal være mellem 1 og 36 måneder');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/members/${member.id || member.email}/grant-pro`,
        { months: m },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success(`Forlænget med ${m} måneder`);
      fetchMembers();
    } catch (error) {
      console.error('Error extending pro:', error);
      toast.error(getErrorMessage(error, 'Kunne ikke forlænge'));
    }
  };

  const handleRevokePro = async (member) => {
    if (!window.confirm(`Fjern manuel Pro-adgang for ${member.name} med det samme?`)) return;
    try {
      await axios.delete(
        `${API}/admin/members/${member.id || member.email}/grant-pro`,
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success('Manuel Pro-adgang fjernet');
      fetchMembers();
    } catch (error) {
      console.error('Error revoking pro:', error);
      toast.error(getErrorMessage(error, 'Kunne ikke fjerne'));
    }
  };

  // A user is "Manual Pro" if any of these holds:
  //   - explicitly granted via /grant-pro (pro_grant_source === 'admin_manual')
  //   - has a manual plan tag (pro_plan starts with 'manual_')
  //   - has role=pro but NO Stripe subscription (i.e. an admin upgraded their role manually
  //     via the dropdown — in this app there's no other non-Stripe path to Pro)
  const isManualPro = (m) =>
    m.pro_grant_source === 'admin_manual' ||
    (typeof m.pro_plan === 'string' && m.pro_plan.startsWith('manual_')) ||
    (m.role === 'pro' && !m.stripe_subscription_id);
  const isStripePro = (m) => !!m.stripe_subscription_id && !isManualPro(m);

  const formatGrantDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (_) { return iso; }
  };

  const isExpired = (iso) => {
    if (!iso) return false;
    try { return new Date(iso) < new Date(); } catch (_) { return false; }
  };

  // Single source of truth for the badge shown in the table.
  // Priority: Admin > Editor > Stripe Pro > Manual Pro > raw role.
  // Backend "role" field is preserved as-is; this only affects display.
  const getEffectiveStatus = (m) => {
    if (m.role === 'admin') {
      return { key: 'admin', label: 'Admin', icon: <FaUserShield />, classes: 'bg-red-100 text-red-700 border-red-200' };
    }
    if (m.role === 'editor') {
      return { key: 'editor', label: 'Redaktør', icon: <FaCrown />, classes: 'bg-purple-100 text-purple-700 border-purple-200' };
    }
    if (isStripePro(m)) {
      const expired = isExpired(m.pro_current_period_end);
      return {
        key: 'stripe',
        label: 'Pro (Stripe)',
        icon: <FaStar />,
        classes: expired ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200',
        sublabel: m.pro_current_period_end ? `${expired ? 'Udløbet ' : 'Fornyes '}${formatGrantDate(m.pro_current_period_end)}` : null,
        sublabelClass: expired ? 'text-red-600 font-semibold' : 'text-gray-500',
      };
    }
    if (isManualPro(m)) {
      const expired = isExpired(m.pro_current_period_end);
      return {
        key: 'manual',
        label: 'Pro (Manuel)',
        icon: <FaGift />,
        classes: expired ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-orange-100 text-orange-700 border-orange-200',
        sublabel: m.pro_current_period_end ? `${expired ? 'Udløbet ' : 'Indtil '}${formatGrantDate(m.pro_current_period_end)}` : null,
        sublabelClass: expired ? 'text-red-600 font-semibold' : 'text-gray-500',
      };
    }
    if (m.role === 'pro') {
      return { key: 'pro', label: 'Pro', icon: <FaStar />, classes: 'bg-green-100 text-green-700 border-green-200' };
    }
    return { key: 'user', label: 'Aktiv', icon: <FaCheckCircle />, classes: 'bg-blue-50 text-blue-700 border-blue-200' };
  };

  const fetchUserDetails = async (userId) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API}/admin/members/${userId}/details`, {
        withCredentials: true
      });
      setUserDetails(response.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Kunne ikke hente bruger detaljer');
    } finally {
      setLoadingDetails(false);
    }
  };

  const openDetailsModal = (member) => {
    setSelectedUser(member);
    setIsDetailsModalOpen(true);
    fetchUserDetails(member.id || member.email);
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase());
    if (roleFilter === 'all') return matchesSearch;
    const eff = getEffectiveStatus(member).key; // admin|editor|stripe|manual|pro|user
    return matchesSearch && eff === roleFilter;
  });

  // Sort: internal users always pinned to top, then apply selected sort.
  const STATUS_ORDER = { admin: 0, editor: 1, stripe: 2, manual: 3, pro: 4, user: 5 };
  const sortedMembers = [...filteredMembers].sort((a, b) => {
    const aInt = isInternalUser(a);
    const bInt = isInternalUser(b);
    if (aInt && !bInt) return -1;
    if (!aInt && bInt) return 1;

    switch (sortBy) {
      case 'name_asc':
        return (a.name || '').localeCompare(b.name || '', 'da', { sensitivity: 'base' });
      case 'name_desc':
        return (b.name || '').localeCompare(a.name || '', 'da', { sensitivity: 'base' });
      case 'date_asc':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'date_desc':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'status': {
        const ak = getEffectiveStatus(a).key;
        const bk = getEffectiveStatus(b).key;
        return (STATUS_ORDER[ak] ?? 99) - (STATUS_ORDER[bk] ?? 99);
      }
      default:
        return 0;
    }
  });

  // Header click handlers toggle direction on same column.
  const toggleNameSort = () =>
    setSortBy((s) => (s === 'name_asc' ? 'name_desc' : 'name_asc'));
  const toggleDateSort = () =>
    setSortBy((s) => (s === 'date_desc' ? 'date_asc' : 'date_desc'));
  const SortIcon = ({ active, direction }) => {
    if (!active) return <FaSort className="inline-block ml-1 opacity-40" size={11} />;
    return direction === 'asc'
      ? <FaSortAmountUp className="inline-block ml-1 text-cyan-600" size={11} />
      : <FaSortAmountDown className="inline-block ml-1 text-cyan-600" size={11} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <FaUsers className="text-cyan-500" />
            Medlemmer
          </h1>
          <p className="text-gray-600">Administrer brugere og roller</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-lg hover:from-cyan-600 hover:to-cyan-700 flex items-center gap-2 font-medium"
        >
          <FaPlus />
          Opret bruger
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Søg efter navn eller email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">Alle statusser</option>
            <option value="admin">Admin</option>
            <option value="editor">Redaktør</option>
            <option value="stripe">Pro (Stripe)</option>
            <option value="manual">Pro (Manuel)</option>
            <option value="pro">Pro (rolle)</option>
            <option value="user">Aktive brugere</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="members-sort-select"
            aria-label="Sortering"
          >
            <option value="date_desc">Oprettet: nyeste først</option>
            <option value="date_asc">Oprettet: ældste først</option>
            <option value="name_asc">Navn: A → Å</option>
            <option value="name_desc">Navn: Å → A</option>
            <option value="status">Status</option>
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-800">{members.length}</div>
            <div className="text-xs text-gray-600">Total</div>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <div className="text-2xl font-bold text-emerald-700">
              {members.filter(m => getEffectiveStatus(m).key === 'stripe').length}
            </div>
            <div className="text-xs text-emerald-600">Pro (Stripe)</div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-700" data-testid="manual-pro-count">
              {members.filter(m => getEffectiveStatus(m).key === 'manual').length}
            </div>
            <div className="text-xs text-orange-600">Pro (Manuel)</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-700">
              {members.filter(m => getEffectiveStatus(m).key === 'editor' || getEffectiveStatus(m).key === 'admin').length}
            </div>
            <div className="text-xs text-purple-600">Admin/Red.</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-700">
              {members.filter(m => getEffectiveStatus(m).key === 'user').length}
            </div>
            <div className="text-xs text-blue-600">Aktive brugere</div>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-600 cursor-pointer select-none hover:text-cyan-700"
                  onClick={toggleNameSort}
                  data-testid="members-sort-name"
                  title="Sortér efter navn"
                >
                  Bruger
                  <SortIcon
                    active={sortBy === 'name_asc' || sortBy === 'name_desc'}
                    direction={sortBy === 'name_asc' ? 'asc' : 'desc'}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Status
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-600 cursor-pointer select-none hover:text-cyan-700"
                  onClick={toggleDateSort}
                  data-testid="members-sort-date"
                  title="Sortér efter oprettelsesdato"
                >
                  Oprettet
                  <SortIcon
                    active={sortBy === 'date_asc' || sortBy === 'date_desc'}
                    direction={sortBy === 'date_asc' ? 'asc' : 'desc'}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedMembers.map((member) => {
                const internal = isInternalUser(member);
                return (
                <tr
                  key={member.id}
                  className={
                    internal
                      ? 'bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400'
                      : 'hover:bg-gray-50'
                  }
                  data-testid={internal ? `internal-user-row-${member.id}` : undefined}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        internal
                          ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                          : 'bg-gradient-to-br from-cyan-500 to-cyan-600'
                      }`}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800 flex items-center gap-2 flex-wrap">
                          {member.name}
                          {internal && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-200 text-amber-900 border border-amber-300"
                              title="Intern / demo bruger"
                            >
                              Intern
                            </span>
                          )}
                        </div>
                        {member.id === user?.id && (
                          <span className="text-xs text-cyan-600">(Dig)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {member.email}
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const eff = getEffectiveStatus(member);
                      return (
                        <div className="flex flex-col gap-1" data-testid={`status-cell-${member.id}`}>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border w-fit ${eff.classes}`}>
                            {eff.icon}
                            {eff.label}
                          </span>
                          {eff.sublabel && (
                            <span className={`text-xs ${eff.sublabelClass}`}>{eff.sublabel}</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(member.created_at).toLocaleDateString('da-DK')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openDetailsModal(member)}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                        title="Se detaljer"
                      >
                        <FaSearch />
                        <span>Se Detaljer</span>
                      </button>
                      {member.id !== user?.id && (
                        <>
                          <select
                            value={member.role}
                            onChange={(e) => updateUserRole(member.id, e.target.value)}
                            className="text-sm border border-gray-200 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            data-testid={`role-select-${member.id}`}
                            title="Rolle (rettigheder)"
                          >
                            <option value="user">User</option>
                            <option value="editor">Redaktør</option>
                            <option value="admin">Admin</option>
                          </select>
                          {/* Subscription-vælger (adskilt fra role).
                              Preview-testfunktion: kombinér frit fx
                              role=User + subscription=Light. */}
                          <select
                            value={member.subscription_type || 'free'}
                            onChange={(e) => updateUserSubscription(member.id, e.target.value)}
                            className="text-sm border border-orange-200 bg-orange-50 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            data-testid={`subscription-select-${member.id}`}
                            title="Abonnement (adskilt fra rolle)"
                          >
                            <option value="free">Free</option>
                            <option value="light">Light</option>
                            <option value="pro">Pro</option>
                          </select>
                          {/* Manual Pro grant actions */}
                          {isManualPro(member) ? (
                            <>
                              <button
                                onClick={() => handleExtendPro(member, 3)}
                                data-testid={`extend-pro-btn-${member.id}`}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                                title="Forlæng manuel Pro-adgang"
                              >
                                <FaPlusCircle />
                              </button>
                              <button
                                onClick={() => handleRevokePro(member)}
                                data-testid={`revoke-pro-btn-${member.id}`}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                title="Fjern manuel Pro-adgang"
                              >
                                <FaTimesCircle />
                              </button>
                            </>
                          ) : (!isStripePro(member) && member.role !== 'admin' && member.role !== 'editor') ? (
                            <button
                              onClick={() => handleGrantPro(member, 6)}
                              data-testid={`grant-pro-btn-${member.id}`}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 rounded-lg shadow-sm transition-all active:scale-95 whitespace-nowrap"
                              title="Giv 6 mdr. gratis Pro-adgang (kampagne)"
                            >
                              <FaGift />
                              <span className="hidden md:inline">6 mdr. gratis Pro</span>
                              <span className="md:hidden">Gratis</span>
                            </button>
                          ) : null}
                          <button
                            onClick={() => {
                              setSelectedUser(member);
                              setIsResetModalOpen(true);
                            }}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                            title="Reset password"
                          >
                            <FaKey />
                          </button>
                          <button
                            onClick={() => handleDeleteMember(member.id, member.name)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Slet medlem"
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedMembers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Ingen medlemmer fundet
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {isDetailsModalOpen && selectedUser && (
        <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Bruger Detaljer: {selectedUser.name}</DialogTitle>
            </DialogHeader>
            
            {loadingDetails ? (
              <div className="flex justify-center py-12">
                <div className="loading-spinner"></div>
              </div>
            ) : userDetails ? (
              <div className="space-y-6">
                {/* User Info Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Rolle</div>
                    <div className="font-semibold">{userDetails.role}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Oprettet</div>
                    <div className="font-semibold">
                      {new Date(userDetails.created_at).toLocaleDateString('da-DK')}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Sidst Aktiv</div>
                    <div className="font-semibold">
                      {userDetails.last_login 
                        ? new Date(userDetails.last_login).toLocaleDateString('da-DK')
                        : 'Aldrig'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Antal Opskrifter</div>
                    <div className="font-semibold">{userDetails.recipes?.length || 0}</div>
                  </div>
                </div>

                {/* TEMPORARY: View Statistics for Testing Period */}
                {userDetails.view_stats && (
                  <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">📊</span>
                      <h3 className="font-bold text-lg">Visningsstatistik (TEST-PERIODE)</h3>
                      <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">Midlertidig</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded">
                        <div className="text-xs text-gray-500 mb-1">Unikke Opskrifter Besøgt</div>
                        <div className="text-2xl font-bold text-cyan-600">
                          {userDetails.view_stats.unique_recipes_viewed}
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded">
                        <div className="text-xs text-gray-500 mb-1">Total Antal Visninger</div>
                        <div className="text-2xl font-bold text-cyan-600">
                          {userDetails.view_stats.total_views}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions/Activity Log */}
                <div>
                  <h3 className="font-bold text-lg mb-3">Handlinger</h3>
                  {userDetails.activities && userDetails.activities.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-semibold">Handling</th>
                            <th className="px-4 py-2 text-left text-sm font-semibold">Tidspunkt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetails.activities.map((activity, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-4 py-2 text-sm">{activity.action}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">
                                {new Date(activity.timestamp).toLocaleString('da-DK')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm py-4 text-center border rounded-lg">
                      Ingen handlinger registreret
                    </div>
                  )}
                </div>

                {/* User's Recipes */}
                {userDetails.recipes && userDetails.recipes.length > 0 && (
                  <div>
                    <h3 className="font-bold text-lg mb-3">Brugerens Opskrifter ({userDetails.recipes.length})</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {userDetails.recipes.map((recipe) => (
                        <div key={recipe.id} className="border rounded-lg p-3 hover:shadow-md transition-shadow">
                          <div className="font-medium">{recipe.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Oprettet: {new Date(recipe.created_at).toLocaleDateString('da-DK')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Favorites */}
                {userDetails.favorites && userDetails.favorites.length > 0 && (
                  <div>
                    <h3 className="font-bold text-lg mb-3">Favoritter ({userDetails.favorites.length})</h3>
                    <div className="flex flex-wrap gap-2">
                      {userDetails.favorites.map((fav, idx) => (
                        <span key={idx} className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">
                          {fav.recipe_name || fav}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Kunne ikke hente bruger detaljer
              </div>
            )}
            
            <div className="flex justify-end mt-6">
              <Button onClick={() => setIsDetailsModalOpen(false)} variant="outline">
                Luk
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create User Modal */}
      {isCreateModalOpen && (
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Opret ny bruger</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="create-name">Navn</Label>
                <Input
                  id="create-name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="create-password">Password</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                  required
                  minLength={6}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="create-role">Rolle</Label>
                <select
                  id="create-role"
                  value={createForm.role}
                  onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="user">User</option>
                  <option value="editor">Redaktør</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Abonnement (Free/Light/Pro) kan justeres i medlemsoversigten efter oprettelse.
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">Opret bruger</Button>
                <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Annuller
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Reset Password Modal */}
      {isResetModalOpen && selectedUser && (
        <Dialog open={isResetModalOpen} onOpenChange={setIsResetModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nulstil password for {selectedUser.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="reset-password">Nyt password</Label>
                <Input
                  id="reset-password"
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Min. 6 tegn"
                  className="mt-1"
                />
              </div>
              <p className="text-sm text-gray-600">
                Dette vil også logge brugeren ud fra alle enheder.
              </p>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">Nulstil password</Button>
                <Button type="button" variant="outline" onClick={() => {
                  setIsResetModalOpen(false);
                  setResetPassword('');
                  setSelectedUser(null);
                }}>
                  Annuller
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MembersPage;
